import type { OptimizelyDecideCallOptions } from '#types/p13n'
import type { OptimizelyDecision } from '@optimizely/optimizely-sdk'
import {
  getOptimizelyCmabAttributes,
  getOptimizelyCmabUrlMatches,
  isOptimizelyCmabStaticExperiment,
  OPTIMIZELY_CMAB_ATTRIBUTE_WAIT_TIMEOUT_MS,
} from '#core/utils/getOptimizelyCmabAttributes'
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk'

const CUSTOM_OPTIMIZELY_COOKIE_PREFIX = 'optiCustomCookie-'
const PDP_SCOPE_ATTRIBUTES = ['onSale', 'isCustoms'] as const

const getCustomCookieAttributes = (cookieHeader: string) => {
  const parsedCookies = cookieHeader
    .split(';')
    .map((cookie) => cookie.trim())
    .filter(Boolean)
    .map((cookie) => cookie.split(/=(.*)/s, 2))
    .map(([name, value = '']) => [decodeURIComponent(name), decodeURIComponent(value)] as const)

  return Object.fromEntries(
    parsedCookies.filter(([name]) => name.startsWith(CUSTOM_OPTIMIZELY_COOKIE_PREFIX))
  )
}

export const useOptimizely = () => {
  const auth = useAuthStore()
  const cart = useCartStore()
  const userStore = useUserStore()
  const route = useRoute()
  const region = useRegion()
  const { width, height } = useWindowSize()
  const optaNonConsentCookie = useCookie('OptanonConsent')
  const userId = useCookie('optimizelyUserId')
  const locationData = {
    country: useCookie<string>('Edgescape-Country')?.value,
    state: useCookie<string>('Edgescape-State')?.value,
    city: useCookie<string>('Edgescape-City')?.value,
    zip: useCookie<string>('Edgescape-Zip')?.value,
    latLong: useCookie<string>('Edgescape-Lat-Long')?.value
  }
  const visitNumber = useCookie('optimizelyVisitNumber')
  const { $feature, $optimizely } = useNuxtApp()
  const optimizelyLandingPage = useCookie('optimizelyLandingPage')
  const landingPath = useState<string>('optimizely-landing-path', () => route.path)

  const decisions = useState<Map<string, OptimizelyDecision | undefined>>('p13n-decisions', () => new Map())
  const activeOptimizelyOverrides = useState<Record<string, { original: unknown, override: unknown }>>('optimizely-overrides', () => ({}))

  const pathProductId = getProductIdFromPath(route.path)?.toUpperCase()
  const isInPdpContext = !!pathProductId
  const productsData = useState<Map<string, any>>('optimizelyProductsData', () => new Map())

  const isWebDisabled = () => isOptimizelyWebDisabled(route.query.disableOptimizely)
  const isFxDisabled = () => isOptimizelyFxDisabled(route.query.disableOptimizely)

  const getCmabBlob = () => import.meta.client ? window.vfaConfig?.optimizelyCmabMap : undefined

  const buildAttributes = () => getOptimizelyAttributes(
    {
      auth,
      cart,
      cmabAttributes: getOptimizelyCmabAttributes(
        landingPath.value,
        getOptimizelyCmabUrlMatches(getCmabBlob())
      ),
      customCookieAttributes: isClient() ? getCustomCookieAttributes(document.cookie) : {},
      landingPage: Boolean(optimizelyLandingPage.value),
      locationData,
      productData: isInPdpContext ? productsData.value.get(pathProductId) : null,
      route,
      userStore,
      visitNumber: Number(visitNumber.value),
      windowSize: { width: width.value, height: height.value },
    }
  )

  const defineUser = ({ shouldLogAttributes = true }: { shouldLogAttributes?: boolean } = {}) => {
    if (isInPdpContext && !productsData.value.has(pathProductId)) {
      const pdpData = usePdp()
      pdpData?.product.value && productsData.value.set(
        pathProductId,
        {
          onSale: pdpData.product.value.onSale,
          isCustoms: pdpData.product.value.dummyCustoms,
        }
      )
    }

    const attributes = buildAttributes()
    if (window && !isWebDisabled()) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'user',
        attributes
      })
    }
    if (shouldLogAttributes) {
      log.info('Optimizely attributes:', attributes)
    }
    const optimizelyLS = attributes
    useLocalStorage('optimizelyLS', optimizelyLS, { mergeDefaults: true }).value = optimizelyLS

    if (isFxDisabled()) return null

    if (!userId.value) {
      log.warn('createUserContext failed: userId not found')
      return null
    }
    try {
      return $optimizely.client?.createUserContext(userId.value, attributes) ?? null
    }
    catch {
      log.warn('CreateUserContext failed')
      return null
    }
  }

  const getTargetingConsent = (groups) => {
    const consent = checkConsentGroup(region === 'EMEA', route.query.preview === 'true', decodeURIComponent(optaNonConsentCookie.value || ''))
    return groups.every((key) => consent[key])
  }

  const hasAttributeKeys = (keys: string[]) => {
    if (!keys.length) return true

    const attributes = buildAttributes()
    return keys.every((key) => {
      const value = attributes[key]
      return value !== undefined && value !== null && value !== ''
    })
  }

  const waitForAttributeKeys = async (keys: string[]) => {
    if (!keys.length || hasAttributeKeys(keys)) return

    await until(() => hasAttributeKeys(keys)).toBeTruthy({
      timeout: OPTIMIZELY_CMAB_ATTRIBUTE_WAIT_TIMEOUT_MS,
      throwOnTimeout: false,
    })
  }

  const decide = async (key: string, options: OptimizelyDecideCallOptions = {}) => {
    const {
      shouldLogAttributes = true,
      async: forceAsync,
      waitForAttributeKeys: cmsAttributeKeys,
      disableDecisionEvent = false,
    } = options
    const blob = getCmabBlob()
    const isStaticCmab = isOptimizelyCmabStaticExperiment(key, blob)
    const useAsync = forceAsync === true || isStaticCmab

    // should not attempt to decide if we are on server
    if (!$feature.configP13n?.enableOptimizely || !isClient() || isFxDisabled()) return

    // if in PDP context, await product data to have all necessary attributes (onSale, isCustoms etc.), if needed
    if (isInPdpContext && PDP_SCOPE_ATTRIBUTES.some((attr) => $optimizely?.isAttributeUsedByFlag(key, attr))) {
      const { product } = usePdp()
      await until(product).toMatch((product) => !!product && product.id.toUpperCase() === pathProductId)

      productsData.value.set(
        pathProductId,
        {
          onSale: product.value?.onSale,
          isCustoms: product.value?.dummyCustoms,
        }
      )
    }

    if (useAsync) {
      const keysToWait = cmsAttributeKeys ?? (isStaticCmab ? $optimizely?.getAttributeKeysUsedByFlag?.(key) ?? [] : [])
      await waitForAttributeKeys(keysToWait)
    }

    const user = getTargetingConsent(['targeting']) ? defineUser({ shouldLogAttributes }) : null
    const decideOptions = [
      OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
      OptimizelyDecideOption.IGNORE_USER_PROFILE_SERVICE,
      ...(disableDecisionEvent ? [OptimizelyDecideOption.DISABLE_DECISION_EVENT] : []),
    ]

    if (!useAsync) {
      decisions.value.set(key, user?.decide(key, decideOptions))
      return
    }

    try {
      decisions.value.set(key, user ? await user.decideAsync(key, decideOptions) : undefined)
    }
    catch (err) {
      log.warn(`Failed to decideAsync Optimizely flag "${key}": ${String(err)}`)
      decisions.value.set(key, undefined)
    }
  }

  const trackEvent = (eventKey: string, properties, decimalRevenue?) => {
    if (!$feature.configP13n?.enableOptimizely) return

    const webDisabled = isWebDisabled()
    const fxDisabled = isFxDisabled()
    if (webDisabled && fxDisabled) return

    const revenue = Math.round(decimalRevenue * 100)
    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    const mappedProperties = mapProperties(eventKey, properties)
    const tags = {
      $opt_event_properties: mappedProperties,
      ...(revenue && { revenue })
    }
    if (window && !webDisabled) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'event',
        eventName: eventKey,
        tags: {
          ...(revenue && { revenue })
        },
        properties: mappedProperties
      })
    }
    log.info(`Tracking ${eventKey} Event for Optimizely: tags were ${JSON.stringify(tags)}`)
    if (!fxDisabled)
      user?.trackEvent(eventKey, tags)
  }

  const activateOptimizely = () => {
    if (!$feature.configOptimizelyWebExperiments.enabled || !isClient() || isWebDisabled()) return
    window.userLoggedIn = auth?.loggedIn || false
    window.optimizely = window.optimizely || []
    window.optimizely.push({
      type: 'activate'
    })
    optimizelyLandingPage.value = ''
  }

  const allDecisionsResolved = useState('p13n-all-decisions-resolved', () => false)

  const decideAll = async ({ shouldLogAttributes = true }: { shouldLogAttributes?: boolean } = {}) => {
    if (!$feature.configP13n?.enableOptimizely || !isClient() || isFxDisabled()) return
    allDecisionsResolved.value = false
    const user = getTargetingConsent(['targeting']) ? defineUser({ shouldLogAttributes }) : null
    const allDecisions = user
      ? user.decideAll([
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
        OptimizelyDecideOption.IGNORE_USER_PROFILE_SERVICE,
        OptimizelyDecideOption.DISABLE_DECISION_EVENT,
      ]) ?? {}
      : {}

    for (const [key, decision] of Object.entries(allDecisions))
      decisions.value.set(key, decision)

    // Temp: log decisions from Opti
    if (import.meta.client)
      console.log('[Optimizely] decideAll decisions', Object.fromEntries(Object.entries(allDecisions).map(([key, decision]) => [key, decision?.variables])))

    allDecisionsResolved.value = true
  }

  return {
    activeOptimizelyOverrides,
    allDecisionsResolved,
    decide,
    decideAll,
    decisions,
    defineUser,
    trackEvent,
    activateOptimizely,
  }
}
